import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  ArrowRight,
  BookOpen,
  TrendingUp,
  Lightbulb,
  Target,
  Zap,
  Home,
  Globe,
  Search,
  Users,
  BarChart3,
  MessageCircle,
  Star,
  Award,
  Smartphone,
  Mail,
  Share2,
  Calendar,
  FileText,
  Filter,
  Tag,
  ExternalLink,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const blogArticles = {
  de: [
    {
      id: 1,
      title:
        "Konvertierende Feedback-Formulare: Aufbau, Fragen & DSGVO in 5 Minuten",
      excerpt:
        "So erstellst du in Reebaq ein Formular, das ausgefüllt wird: optimale Frage-Typen, logische Sprünge, Einwilligung & Branding.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Warum Formulardesign über Teilnahme entscheidet</h2>
        <p class="mb-4">Ein gutes Formular ist kurz, mobilfreundlich und klar. In Reebaq baust du in Minuten ein Setup, das Nutzer wirklich abschließen.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Bausteine, die funktionieren</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Fragetypen mixen:</strong> NPS/CSAT, Likert-Skalen, 1 Freitext für echtes „Warum?"</li>
          <li><strong>Logische Sprünge:</strong> Nur relevante Fragen zeigen → weniger Abbruch</li>
          <li><strong>Branding:</strong> Logo, Farben, freundlicher Intro-Text (max. 2 Sätze)</li>
          <li><strong>Progress:</strong> „Dauert 30 Sekunden" macht Abschlüsse messbar höher</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">DSGVO & Vertrauen</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Transparenz:</strong> Zweck klar nennen (z. B. Produktverbesserung, Support)</li>
          <li><strong>Einwilligung:</strong> Checkbox & Hinweis auf Löschung/Auskunft</li>
          <li><strong>EU-Hosting:</strong> Reebaq speichert innerhalb der EU</li>
        </ul>

        <p class="mb-4">Pro-Tipp: Max. 5 Fragen + 1 Freitext. Besser wöchentlich messen als einmal im Jahr alles auf einmal.</p>
      `,
      readTime: "5 Min",
      category: "Formulare & UX",
      icon: BookOpen,
      gradient: "from-orange-500 to-amber-500",
      tags: ["Feedback-Formulare", "DSGVO", "UX-Design", "Conversion"],
      author: "Sarah Weber",
      publishDate: "2024-03-15",
    },
    {
      id: 2,
      title: "Mehr Antworten mit QR-Codes & Links: Placement, CTA & Tracking",
      excerpt:
        "So holst du am Point-of-Experience sofort Feedback: QR-Codes richtig platzieren, CTAs formulieren und UTM-Quellen messen.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Der Scan ist die neue Abkürzung</h2>
        <p class="mb-4">Mit Reebaq generierst du zu jedem Formular QR-Codes & Kurzlinks. Je kürzer der Weg, desto höher die Teilnahme.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Best Practices für mehr Scans</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>CTAs sichtbar:</strong> „30 Sek. Feedback – Danke!" oder „Hilf uns besser zu werden"</li>
          <li><strong>Placement:</strong> Kasse, Rechnung, Tischaufsteller, Verpackung, After-Delivery-Mail</li>
          <li><strong>Mobile-First:</strong> Seiten ohne Scroll-Hürden, große Buttons</li>
          <li><strong>UTM-Tracking:</strong> Quelle/Standort vergleichen (z. B. Filiale A vs. B)</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Conversion-Quickwins</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li>QR + kurzer Link (für Fälle, in denen nicht gescannt werden kann)</li>
          <li>Ein Satz Mehrwert: „Mit deinem Feedback verbessern wir …"</li>
          <li>Anreiz optional (siehe Gewinnspiele), aber nicht zwingend</li>
        </ul>
      `,
      readTime: "4 Min",
      category: "Erhebung & Marketing",
      icon: Target,
      gradient: "from-orange-600 to-amber-500",
      tags: ["QR-Codes", "Mobile Marketing", "CTA-Optimierung", "UTM-Tracking"],
      author: "Michael Schmidt",
      publishDate: "2024-03-10",
    },
    {
      id: 3,
      title: "Dashboard & Reporting: Diese KPIs brauchst du wirklich",
      excerpt:
        "Von NPS/CSAT bis Antwortquote: Die wichtigsten Kennzahlen im Reebaq-Dashboard – inklusive Segmenten & Alerts.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Fokus auf Entscheidungen, nicht auf Chart-Zoo</h2>
        <p class="mb-4">Reebaq zeigt dir, was zählt – Überblicke, Trends und Ausreißer. Weniger Metriken, mehr Wirkung.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Kern-KPIs</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>NPS/CSAT/CES:</strong> Stimmung & Aufwand sichtbar machen</li>
          <li><strong>Antwortquote & Completion:</strong> Reichweite und Formular-Qualität</li>
          <li><strong>Top-Themen:</strong> häufigste Gründe pro Segment/Standort</li>
          <li><strong>Time-to-Close-Loop:</strong> Zeit von Feedback bis Antwort/Aktion</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Segmente & Drill-Down</h3>
        <p class="mb-4">Vergleiche Kanäle (QR vs. Mail), Produkte, Filialen oder Zeiträume. Mit einem Klick in die Kommentare springen.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Exports & Sharing</h3>
        <p class="mb-4">Export als PDF/CSV, geplante Reports per Mail fürs Team – perfekt fürs Weekly.</p>
      `,
      readTime: "6 Min",
      category: "Analytics & KPIs",
      icon: TrendingUp,
      gradient: "from-amber-500 to-orange-500",
      tags: ["Dashboard", "KPIs", "Analytics", "Reporting"],
      author: "Anna Müller",
      publishDate: "2024-03-05",
    },
    {
      id: 4,
      title: "KI-Analyse: Sentiment, Themen & Prioritäten automatisch erkennen",
      excerpt:
        "Wie Reebaq aus Freitext echte Maßnahmen ableitet: Emotion, Themen-Clustering und Priorisierung für Roadmap & Service.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Aus Text wird Insight – automatisch</h2>
        <p class="mb-4">Die KI in Reebaq liest Freitexte, erkennt Stimmung und gruppiert Themen. So priorisierst du, was den größten Effekt hat.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Was die KI liefert</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Sentiment:</strong> positiv, neutral, negativ – inklusive Begründungen</li>
          <li><strong>Topics & Trends:</strong> wiederkehrende Muster über Wochen/Monate</li>
          <li><strong>Priorisierung:</strong> Hebel mit größtem Kundennutzen zuerst</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Von Insight zu Action</h3>
        <p class="mb-4">Erstelle Maßnahmen, weise sie Teams zu und tracke die Wirkung im Zeitverlauf – direkt im Dashboard.</p>
      `,
      readTime: "5 Min",
      category: "KI & Auswertung",
      icon: Zap,
      gradient: "from-orange-500 to-red-500",
      tags: [
        "KI-Analyse",
        "Sentiment Analysis",
        "Machine Learning",
        "Automatisierung",
      ],
      author: "Dr. Thomas Klein",
      publishDate: "2024-02-28",
    },
    {
      id: 5,
      title: "Mehr Teilnahme mit Gewinnspielen – rechtssicher & effektiv",
      excerpt:
        "Gamification als Turbo für Rücklaufquoten: Preise, Transparenz & DSGVO – plus Best-Practice-Vorlagen.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Spieltrieb nutzen – seriös und messbar</h2>
        <p class="mb-4">Mit Reebaq kannst du optional Gewinnspiele koppeln. Das steigert Rückläufe deutlich – ohne die Datenqualität zu verwässern.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">So setzt du es auf</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Relevante Preise:</strong> passend zur Zielgruppe (z. B. Produktgutschrift)</li>
          <li><strong>Einfache Teilnahme:</strong> Teilnahme automatisch nach Absenden</li>
          <li><strong>Klare Regeln:</strong> Teilnahmebedingungen & Laufzeit transparent</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">DSGVO beachten</h3>
        <p class="mb-4">Zweck, Einwilligung und Dauer klar nennen. Gewinnerauswahl dokumentieren – Reebaq unterstützt bei Export & Löschung.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Messung</h3>
        <p class="mb-4">Vergleiche Antwortquote mit/ohne Anreiz, Segmentierung nach Quelle (QR vs. Link) und beobachte Freitext-Qualität.</p>
      `,
      readTime: "5 Min",
      category: "Engagement",
      icon: Lightbulb,
      gradient: "from-orange-500 to-yellow-500",
      tags: ["Gamification", "Gewinnspiele", "Engagement", "DSGVO"],
      author: "Lisa Fischer",
      publishDate: "2024-02-20",
    },
    {
      id: 6,
      title: "Mobile-First Feedback: Optimierung für Smartphone-Nutzer",
      excerpt:
        "Über 80% der Feedback-Eingaben erfolgen mobil. Hier erfährst du, wie du deine Formulare für Smartphones optimierst und die Conversion-Rate steigerst.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Mobile ist der neue Standard</h2>
        <p class="mb-4">Feedback-Management muss heute mobile-first gedacht werden. Die meisten Nutzer geben ihr Feedback unterwegs über das Smartphone ab.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Design-Prinzipien für Mobile</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Touch-optimiert:</strong> Große Buttons (min. 44px), ausreichend Abstand</li>
          <li><strong>Einfache Navigation:</strong> Wenige Schritte, klare Fortschrittsanzeige</li>
          <li><strong>Schnelle Ladezeiten:</strong> Kompakte Grafiken, optimierte Performance</li>
          <li><strong>Lesbare Schrift:</strong> Mindestens 16px Schriftgröße für gute Lesbarkeit</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Technische Optimierungen</h3>
        <p class="mb-4">PWA-Features nutzen, Offline-Fähigkeiten implementieren und nahtlose Integration in bestehende Apps ermöglichen.</p>
      `,
      readTime: "4 Min",
      category: "Mobile & UX",
      icon: Smartphone,
      gradient: "from-blue-500 to-cyan-500",
      tags: [
        "Mobile Optimierung",
        "Responsive Design",
        "PWA",
        "Touch Interface",
      ],
      author: "Marc Weber",
      publishDate: "2024-02-15",
    },
  ],
  en: [
    {
      id: 1,
      title:
        "Converting Feedback Forms: Structure, Questions & GDPR in 5 Minutes",
      excerpt:
        "How to create forms in Reebaq that actually get completed: optimal question types, logical jumps, consent & branding.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Why Form Design Determines Participation</h2>
        <p class="mb-4">A good form is short, mobile-friendly, and clear. With Reebaq, you can build a setup in minutes that users actually complete.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Building Blocks That Work</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Mix question types:</strong> NPS/CSAT, Likert scales, 1 free text for the real "why?"</li>
          <li><strong>Logical jumps:</strong> Show only relevant questions → less abandonment</li>
          <li><strong>Branding:</strong> Logo, colors, friendly intro text (max. 2 sentences)</li>
          <li><strong>Progress:</strong> "Takes 30 seconds" makes completion rates measurably higher</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">GDPR & Trust</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Transparency:</strong> Clearly state purpose (e.g., product improvement, support)</li>
          <li><strong>Consent:</strong> Checkbox & notice about deletion/disclosure</li>
          <li><strong>EU Hosting:</strong> Reebaq stores within the EU</li>
        </ul>

        <p class="mb-4">Pro tip: Max. 5 questions + 1 free text. Better to measure weekly than everything at once annually.</p>
      `,
      readTime: "5 min",
      category: "Forms & UX",
      icon: BookOpen,
      gradient: "from-orange-500 to-amber-500",
      tags: ["Feedback Forms", "GDPR", "UX Design", "Conversion"],
      author: "Sarah Weber",
      publishDate: "2024-03-15",
    },
    {
      id: 2,
      title: "More Responses with QR Codes & Links: Placement, CTA & Tracking",
      excerpt:
        "How to get instant feedback at the point of experience: placing QR codes correctly, formulating CTAs, and measuring UTM sources.",
      content: `
        <h2 class="text-2xl font-bold mb-4">The Scan is the New Shortcut</h2>
        <p class="mb-4">With Reebaq, you generate QR codes & short links for every form. The shorter the path, the higher the participation.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Best Practices for More Scans</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Visible CTAs:</strong> "30 sec feedback – Thanks!" or "Help us improve"</li>
          <li><strong>Placement:</strong> Checkout, receipt, table tent, packaging, after-delivery email</li>
          <li><strong>Mobile-first:</strong> Pages without scroll hurdles, large buttons</li>
          <li><strong>UTM tracking:</strong> Compare source/location (e.g., branch A vs. B)</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Conversion Quick Wins</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li>QR + short link (for cases where scanning isn't possible)</li>
          <li>One sentence of value: "With your feedback, we improve..."</li>
          <li>Incentive optional (see sweepstakes), but not mandatory</li>
        </ul>
      `,
      readTime: "4 min",
      category: "Collection & Marketing",
      icon: Target,
      gradient: "from-orange-600 to-amber-500",
      tags: [
        "QR Codes",
        "Mobile Marketing",
        "CTA Optimization",
        "UTM Tracking",
      ],
      author: "Michael Schmidt",
      publishDate: "2024-03-10",
    },
    {
      id: 3,
      title: "Dashboard & Reporting: The KPIs You Actually Need",
      excerpt:
        "From NPS/CSAT to response rate: The most important metrics in the Reebaq dashboard – including segments & alerts.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Focus on Decisions, Not Chart Zoo</h2>
        <p class="mb-4">Reebaq shows you what matters – overviews, trends, and outliers. Fewer metrics, more impact.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Core KPIs</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>NPS/CSAT/CES:</strong> Make sentiment & effort visible</li>
          <li><strong>Response rate & completion:</strong> Reach and form quality</li>
          <li><strong>Top topics:</strong> Most common reasons per segment/location</li>
          <li><strong>Time-to-close-loop:</strong> Time from feedback to response/action</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Segments & Drill-down</h3>
        <p class="mb-4">Compare channels (QR vs. email), products, branches, or time periods. Jump into comments with one click.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Exports & Sharing</h3>
        <p class="mb-4">Export as PDF/CSV, scheduled reports via email for the team – perfect for weeklies.</p>
      `,
      readTime: "6 min",
      category: "Analytics & KPIs",
      icon: TrendingUp,
      gradient: "from-amber-500 to-orange-500",
      tags: ["Dashboard", "KPIs", "Analytics", "Reporting"],
      author: "Anna Müller",
      publishDate: "2024-03-05",
    },
    {
      id: 4,
      title: "AI Analysis: Automatically Detect Sentiment, Topics & Priorities",
      excerpt:
        "How Reebaq derives real actions from free text: emotion, topic clustering, and prioritization for roadmap & service.",
      content: `
        <h2 class="text-2xl font-bold mb-4">From Text to Insight – Automatically</h2>
        <p class="mb-4">The AI in Reebaq reads free text, recognizes sentiment, and groups topics. This way you prioritize what has the biggest impact.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">What AI Delivers</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Sentiment:</strong> Positive, neutral, negative – including reasoning</li>
          <li><strong>Topics & trends:</strong> Recurring patterns over weeks/months</li>
          <li><strong>Prioritization:</strong> Levers with greatest customer benefit first</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">From Insight to Action</h3>
        <p class="mb-4">Create actions, assign them to teams, and track impact over time – directly in the dashboard.</p>
      `,
      readTime: "5 min",
      category: "AI & Analysis",
      icon: Zap,
      gradient: "from-orange-500 to-red-500",
      tags: [
        "AI Analysis",
        "Sentiment Analysis",
        "Machine Learning",
        "Automation",
      ],
      author: "Dr. Thomas Klein",
      publishDate: "2024-02-28",
    },
    {
      id: 5,
      title:
        "More Participation with Sweepstakes – Legally Compliant & Effective",
      excerpt:
        "Gamification as a turbo for response rates: prizes, transparency & GDPR – plus best-practice templates.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Use Gaming Instinct – Seriously and Measurably</h2>
        <p class="mb-4">With Reebaq, you can optionally add sweepstakes. This significantly increases returns – without diluting data quality.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">How to Set It Up</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Relevant prizes:</strong> Matching the target group (e.g., product voucher)</li>
          <li><strong>Simple participation:</strong> Automatic participation after submission</li>
          <li><strong>Clear rules:</strong> Terms & conditions and duration transparent</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Consider GDPR</h3>
        <p class="mb-4">Clearly state purpose, consent, and duration. Document winner selection – Reebaq supports export & deletion.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Measurement</h3>
        <p class="mb-4">Compare response rate with/without incentive, segmentation by source (QR vs. link) and observe free text quality.</p>
      `,
      readTime: "5 min",
      category: "Engagement",
      icon: Lightbulb,
      gradient: "from-orange-500 to-yellow-500",
      tags: ["Gamification", "Sweepstakes", "Engagement", "GDPR"],
      author: "Lisa Fischer",
      publishDate: "2024-02-20",
    },
    {
      id: 6,
      title: "Mobile-First Feedback: Optimization for Smartphone Users",
      excerpt:
        "Over 80% of feedback submissions happen on mobile. Learn how to optimize your forms for smartphones and increase conversion rates.",
      content: `
        <h2 class="text-2xl font-bold mb-4">Mobile is the New Standard</h2>
        <p class="mb-4">Feedback management must be thought mobile-first today. Most users provide their feedback on the go via smartphone.</p>

        <h3 class="text-xl font-semibold mb-3 mt-6">Design Principles for Mobile</h3>
        <ul class="list-disc ml-6 mb-4 space-y-2">
          <li><strong>Touch-optimized:</strong> Large buttons (min. 44px), sufficient spacing</li>
          <li><strong>Simple navigation:</strong> Few steps, clear progress indicator</li>
          <li><strong>Fast loading times:</strong> Compact graphics, optimized performance</li>
          <li><strong>Readable text:</strong> Minimum 16px font size for good readability</li>
        </ul>

        <h3 class="text-xl font-semibold mb-3 mt-6">Technical Optimizations</h3>
        <p class="mb-4">Use PWA features, implement offline capabilities, and enable seamless integration into existing apps.</p>
      `,
      readTime: "4 min",
      category: "Mobile & UX",
      icon: Smartphone,
      gradient: "from-blue-500 to-cyan-500",
      tags: [
        "Mobile Optimization",
        "Responsive Design",
        "PWA",
        "Touch Interface",
      ],
      author: "Marc Weber",
      publishDate: "2024-02-15",
    },
  ],
};

const translations = {
  de: {
    blogTitle: "Feedback Blog",
    headerTitle: "Insights & Trends im",
    headerHighlight: "Feedback-Management",
    headerDescription:
      "Entdecken Sie die neuesten Entwicklungen, Best Practices und innovative Ansätze für erfolgreiches Feedback-Management in der digitalen Welt. Lernen Sie, wie moderne Unternehmen Kundenfeedback strategisch nutzen, um Produkte zu verbessern, Kundenzufriedenheit zu steigern und nachhaltige Geschäftserfolge zu erzielen.",
    readArticle: "Artikel lesen",
    backToHome: "Zurück zur Startseite",
    searchPlaceholder: "Artikel durchsuchen...",
    filterByCategory: "Nach Kategorie filtern",
    allCategories: "Alle Kategorien",
    latestArticles: "Neueste Artikel",
    popularTags: "Beliebte Tags",
    authorBy: "Von",
    publishedOn: "Veröffentlicht am",
    relatedArticles: "Ähnliche Artikel",
    shareArticle: "Artikel teilen",
    newsletter: "Newsletter abonnieren",
    newsletterText:
      "Bleiben Sie auf dem Laufenden mit den neuesten Trends im Feedback-Management",
    subscribe: "Abonnieren",
    aboutReebaq: "Über Reebaq",
    aboutText:
      "Reebaq ist die führende Plattform für intelligentes Feedback-Management. Wir helfen Unternehmen dabei, Kundenfeedback systematisch zu sammeln, zu analysieren und in konkrete Verbesserungen umzusetzen.",
    learnMore: "Mehr erfahren",
    getStarted: "Jetzt starten",
    categories: "Kategorien",
    insights: "Insights",
    trends: "Trends",
    management: "Management",
    feedback: "Feedback",
    dashboard: "Dashboard",
    analytics: "Analytics",
    mobile: "Mobile",
    optimization: "Optimierung",
    engagement: "Engagement",
    conversion: "Conversion",
  },
  en: {
    blogTitle: "Feedback Blog",
    headerTitle: "Insights & Trends in",
    headerHighlight: "Feedback Management",
    headerDescription:
      "Discover the latest developments, best practices, and innovative approaches for successful feedback management in the digital world. Learn how modern companies strategically use customer feedback to improve products, increase customer satisfaction, and achieve sustainable business success.",
    readArticle: "Read Article",
    backToHome: "Back to Home",
    searchPlaceholder: "Search articles...",
    filterByCategory: "Filter by category",
    allCategories: "All Categories",
    latestArticles: "Latest Articles",
    popularTags: "Popular Tags",
    authorBy: "By",
    publishedOn: "Published on",
    relatedArticles: "Related Articles",
    shareArticle: "Share Article",
    newsletter: "Subscribe to Newsletter",
    newsletterText:
      "Stay updated with the latest trends in feedback management",
    subscribe: "Subscribe",
    aboutReebaq: "About Reebaq",
    aboutText:
      "Reebaq is the leading platform for intelligent feedback management. We help companies systematically collect, analyze, and transform customer feedback into concrete improvements.",
    learnMore: "Learn More",
    getStarted: "Get Started",
    categories: "Categories",
    insights: "Insights",
    trends: "Trends",
    management: "Management",
    feedback: "Feedback",
    dashboard: "Dashboard",
    analytics: "Analytics",
    mobile: "Mobile",
    optimization: "Optimization",
    engagement: "Engagement",
    conversion: "Conversion",
  },
};

export default function BlogPage() {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [email, setEmail] = useState("");
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    // Marker für Prerender-Script
    document.body.setAttribute("data-page", "blog");
    document.body.setAttribute("data-loaded", "true");

    // Spinner für 0.5 Sekunden anzeigen
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => {
      document.body.removeAttribute("data-page");
      document.body.removeAttribute("data-loaded");
      clearTimeout(timer);
    };
  }, []);

  const t = translations[language];
  const articles = blogArticles[language];

  // Filter articles based on search and category
  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      searchTerm === "" ||
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      selectedCategory === "all" || article.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = [...new Set(articles.map((article) => article.category))];

  // Get popular tags
  const allTags = articles.flatMap((article) => article.tags);
  const tagCounts = allTags.reduce((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {});
  const popularTags = Object.entries(tagCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([tag]) => tag);

  const handleHomeClick = () => {
    // In a real app, this would use router navigation
    window.location.href = "/";
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log("Newsletter subscription:", email);
    setEmail("");
  };

  // Get related articles (same category, excluding current)
  const getRelatedArticles = (currentArticle) => {
    return articles
      .filter(
        (article) =>
          article.id !== currentArticle.id &&
          article.category === currentArticle.category
      )
      .slice(0, 3);
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const badgeVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut",
      },
    },
  };

  const descriptionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.4,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const navigationVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const floatingElementVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 20,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-orange-25 to-amber-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(251, 146, 60, 0.1) 0%, transparent 50%), 
                           radial-gradient(circle at 75% 75%, rgba(251, 146, 60, 0.08) 0%, transparent 50%),
                           radial-gradient(circle at 50% 0%, rgba(255, 167, 38, 0.05) 0%, transparent 50%)`,
          }}
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-orange-200/25 to-orange-300/15 rounded-full blur-3xl animate-pulse"
          variants={floatingElementVariants}
          initial="hidden"
          animate="visible"
        />
        <motion.div
          className="absolute bottom-32 right-16 w-80 h-80 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl animate-pulse"
          variants={floatingElementVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
          style={{ animationDelay: "2s" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-64 h-64 bg-gradient-to-br from-orange-100/30 to-amber-100/25 rounded-full blur-2xl animate-pulse"
          variants={floatingElementVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.6 }}
          style={{ animationDelay: "4s" }}
        />

        {/* Subtle geometric shapes */}
        <motion.div
          className="absolute top-40 right-1/4 w-32 h-32 bg-orange-200/10 rotate-45 rounded-lg blur-sm"
          variants={floatingElementVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.4 }}
        />
        <motion.div
          className="absolute bottom-40 left-1/4 w-24 h-24 bg-amber-200/15 rotate-12 rounded-lg blur-sm"
          variants={floatingElementVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.7 }}
        />
      </div>

      {/* Navigation Header */}
      <motion.div
        className="relative z-10 flex justify-between items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8"
        variants={navigationVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Home Button */}
        <motion.button
          onClick={handleHomeClick}
          className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
          <span className="hidden sm:inline">{t.backToHome}</span>
        </motion.button>

        {/* Language Toggle */}
        <motion.button
          onClick={toggleLanguage}
          className="group flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm hover:bg-white/90 border border-orange-200 hover:border-orange-300 text-orange-700 hover:text-orange-800 rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Globe className="w-4 h-4 group-hover:rotate-12 transition-transform" />
          <span className="text-sm font-semibold">
            {language === "de" ? "EN" : "DE"}
          </span>
        </motion.button>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6 border border-orange-200"
            variants={badgeVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <BookOpen className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-semibold text-orange-700">
              {t.blogTitle}
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6"
            variants={titleVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {t.headerTitle}{" "}
            <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
              {t.headerHighlight}
            </span>
          </motion.h1>

          <motion.p
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
            variants={descriptionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {t.headerDescription}
          </motion.p>
        </motion.div>

        {/* Search and Filter Section */}
        <motion.div
          className="mb-12 space-y-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Search Bar */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === "all"
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-700 hover:bg-orange-50 border border-orange-200"
              }`}
            >
              {t.allCategories}
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-orange-500 text-white"
                    : "bg-white text-gray-700 hover:bg-orange-50 border border-orange-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Blog Articles Grid */}
            <motion.div
              className="grid md:grid-cols-2 gap-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {filteredArticles.map((article, index) => {
                const IconComponent = article.icon;
                return (
                  <motion.div
                    key={article.id}
                    className="group cursor-pointer transition-all duration-300 hover:shadow-xl bg-white rounded-lg border-0 overflow-hidden h-full flex flex-col shadow-sm"
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{
                      scale: 1.02,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedArticle(article)}
                  >
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Category Badge and Icon */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="bg-orange-100 text-orange-700 border border-orange-200 px-2 py-1 rounded text-xs font-medium">
                          {article.category}
                        </span>
                        <motion.div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-r ${article.gradient} p-2 flex items-center justify-center transition-transform`}
                          whileHover={{
                            scale: 1.1,
                            rotate: 5,
                            transition: { duration: 0.2 },
                          }}
                        >
                          <IconComponent className="w-5 h-5 text-white" />
                        </motion.div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-600 mb-4 flex-1 line-clamp-3 leading-relaxed">
                        {article.excerpt}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1 mb-4">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Meta Information */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{article.readTime}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(article.publishDate).toLocaleDateString(
                              language
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Read More Button */}
                      <motion.button
                        className="w-full flex items-center justify-between py-2 px-4 rounded-md group-hover:bg-orange-50 group-hover:text-orange-600 transition-colors text-gray-700"
                        whileHover={{
                          backgroundColor: "rgba(254, 215, 170, 0.1)",
                          transition: { duration: 0.2 },
                        }}
                      >
                        {t.readArticle}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* No Results */}
            {filteredArticles.length === 0 && (
              <motion.div
                className="text-center py-12"
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  {language === "de"
                    ? "Keine Artikel gefunden"
                    : "No articles found"}
                </h3>
                <p className="text-gray-500">
                  {language === "de"
                    ? "Versuchen Sie andere Suchbegriffe oder Filter."
                    : "Try different search terms or filters."}
                </p>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-8">
            {/* Popular Tags */}
            <motion.div
              className="bg-white rounded-lg p-6 shadow-sm border border-orange-100"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-orange-500" />
                {t.popularTags}
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSearchTerm(tag)}
                    className="bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium transition-colors border border-orange-200"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Newsletter Signup */}
            <motion.div
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white shadow-lg"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                {language === "de" ? "Jetzt starten" : "Get Started"}
              </h3>
              <p className="text-orange-100 mb-4 text-sm">
                {language === "de"
                  ? "Erstellen Sie Ihr erstes Feedback-Formular in unter 2 Minuten"
                  : "Create your first feedback form in under 2 minutes"}
              </p>
              <a
                href="/login"
                className="w-full bg-white text-orange-600 px-4 py-2 rounded-lg font-medium hover:bg-orange-50 transition-colors block text-center"
              >
                {language === "de" ? "Kostenlos anmelden" : "Sign up free"}
              </a>
            </motion.div>

            {/* About Reebaq */}
            <motion.div
              className="bg-white rounded-lg p-6 shadow-sm border border-orange-100"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-orange-500" />
                {t.aboutReebaq}
              </h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                {t.aboutText}
              </p>
              <div className="space-y-2">
                <button
                  onClick={handleHomeClick}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t.learnMore}
                </button>
              </div>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              className="bg-white rounded-lg p-6 shadow-sm border border-orange-100"
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                {t.insights}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t.categories}</span>
                  <span className="text-lg font-semibold text-orange-600">
                    {categories.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t.trends}</span>
                  <span className="text-lg font-semibold text-orange-600">
                    {articles.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t.engagement}</span>
                  <span className="text-lg font-semibold text-orange-600">
                    95%
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* SEO Content Section */}
        <motion.div
          className="mt-16 bg-white rounded-lg p-8 shadow-sm border border-orange-100"
          variants={cardVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {language === "de"
                ? "Warum professionelles Feedback-Management entscheidend ist"
                : "Why Professional Feedback Management is Crucial"}
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  {language === "de" ? "Moderne Analytics" : "Modern Analytics"}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {language === "de"
                    ? "Professionelle Feedback-Management-Systeme verwenden fortschrittliche Analytics, um aus Kundendaten verwertbare Insights zu generieren. Dashboard-Tools ermöglichen es Unternehmen, Trends in Echtzeit zu verfolgen und datengestützte Entscheidungen zu treffen."
                    : "Professional feedback management systems use advanced analytics to generate actionable insights from customer data. Dashboard tools enable companies to track trends in real-time and make data-driven decisions."}
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-orange-500" />
                  {language === "de"
                    ? "Mobile Optimierung"
                    : "Mobile Optimization"}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {language === "de"
                    ? "Da über 80% der Feedback-Eingaben über mobile Geräte erfolgen, ist eine mobile-first Strategie unerlässlich. Responsive Design und intuitive Touch-Interfaces maximieren die Conversion-Rates und verbessern die Nutzererfahrung erheblich."
                    : "With over 80% of feedback submissions happening on mobile devices, a mobile-first strategy is essential. Responsive design and intuitive touch interfaces maximize conversion rates and significantly improve user experience."}
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  {language === "de"
                    ? "KI-gestützte Auswertung"
                    : "AI-Powered Analysis"}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {language === "de"
                    ? "Künstliche Intelligenz revolutioniert die Feedback-Analyse durch automatische Sentiment-Erkennung, Themen-Clustering und Prioritätsbewertung. Diese Technologien ermöglichen es Unternehmen, große Mengen an Kundenfeedback effizient zu verarbeiten und die wichtigsten Verbesserungsbereiche zu identifizieren."
                    : "Artificial intelligence revolutionizes feedback analysis through automatic sentiment detection, topic clustering, and priority assessment. These technologies enable companies to efficiently process large volumes of customer feedback and identify the most important areas for improvement."}
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  {language === "de"
                    ? "Zielgerichtete Strategien"
                    : "Targeted Strategies"}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {language === "de"
                    ? "Erfolgreiche Feedback-Kampagnen nutzen gezielte Placement-Strategien, optimierte CTAs und durchdachtes UTM-Tracking. QR-Codes und Kurzlinks am Point-of-Experience sorgen für sofortige Reaktionen und höhere Engagement-Raten bei der Zielgruppe."
                    : "Successful feedback campaigns use targeted placement strategies, optimized CTAs, and thoughtful UTM tracking. QR codes and short links at the point of experience ensure immediate responses and higher engagement rates with the target audience."}
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg text-gray-700 mb-6">
                {language === "de"
                  ? "Entdecken Sie, wie moderne Unternehmen mit intelligentem Feedback-Management ihre Kundenzufriedenheit steigern und nachhaltigen Geschäftserfolg erzielen."
                  : "Discover how modern companies use intelligent feedback management to increase customer satisfaction and achieve sustainable business success."}
              </p>
              <button
                onClick={handleHomeClick}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
              >
                {t.getStarted}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={() => setSelectedArticle(null)}
        >
          <motion.div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="space-y-4 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="bg-orange-100 text-orange-700 border border-orange-200 px-3 py-1 rounded-full text-sm font-medium">
                      {selectedArticle.category}
                    </span>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>{selectedArticle.readTime}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(
                          selectedArticle.publishDate
                        ).toLocaleDateString(language)}
                      </span>
                    </div>
                  </div>

                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                    {selectedArticle.title}
                  </h2>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {selectedArticle.tags.map((tag) => (
                      <span
                        key={tag}
                        className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {/* Share Button */}
                  <motion.button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: selectedArticle.title,
                          text: selectedArticle.excerpt,
                          url: window.location.href,
                        });
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title={t.shareArticle}
                  >
                    <Share2 className="w-5 h-5" />
                  </motion.button>

                  {/* Close Button */}
                  <motion.button
                    onClick={() => setSelectedArticle(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-xl">×</span>
                  </motion.button>
                </div>
              </div>

              {/* Content */}
              <div className="mb-8">
                <div
                  className="prose prose-lg max-w-none text-gray-700 prose-headings:text-gray-900 prose-links:text-orange-600 prose-strong:text-gray-900"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </div>

              {/* Related Articles */}
              {getRelatedArticles(selectedArticle).length > 0 && (
                <div className="border-t border-gray-200 pt-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-500" />
                    {t.relatedArticles}
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {getRelatedArticles(selectedArticle).map((article) => {
                      const IconComponent = article.icon;
                      return (
                        <motion.div
                          key={article.id}
                          className="group cursor-pointer p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-all border border-gray-200 hover:border-orange-200"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedArticle(article)}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div
                              className={`w-8 h-8 rounded-lg bg-gradient-to-r ${article.gradient} p-1.5 flex items-center justify-center`}
                            >
                              <IconComponent className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-xs text-gray-500 font-medium">
                              {article.category}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors text-sm line-clamp-2 mb-2">
                            {article.title}
                          </h4>
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{article.readTime}</span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Footer with Internal Links */}
      <motion.footer
        className="relative z-10 bg-white border-t border-orange-100 mt-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t.aboutReebaq}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {t.aboutText}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleHomeClick}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t.getStarted}
                </button>
                <button
                  onClick={handleHomeClick}
                  className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t.learnMore}
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                {language === "de" ? "Kategorien" : "Categories"}
              </h4>
              <ul className="space-y-2">
                {categories.slice(0, 5).map((category) => (
                  <li key={category}>
                    <button
                      onClick={() => {
                        setSelectedCategory(category);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="text-gray-600 hover:text-orange-600 transition-colors text-sm"
                    >
                      {category}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Popular Topics */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                {language === "de" ? "Beliebte Themen" : "Popular Topics"}
              </h4>
              <ul className="space-y-2">
                {popularTags.slice(0, 5).map((tag) => (
                  <li key={tag}>
                    <button
                      onClick={() => {
                        setSearchTerm(tag);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="text-gray-600 hover:text-orange-600 transition-colors text-sm"
                    >
                      {tag}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 Reebaq.{" "}
              {language === "de"
                ? "Alle Rechte vorbehalten."
                : "All rights reserved."}
            </p>
            <div className="flex gap-6">
              <a
                href="/privacypolicy"
                className="text-gray-500 hover:text-orange-600 text-sm transition-colors"
              >
                {language === "de" ? "Datenschutz" : "Privacy"}
              </a>
              <a
                href="/impressum"
                className="text-gray-500 hover:text-orange-600 text-sm transition-colors"
              >
                {language === "de" ? "Impressum" : "Legal"}
              </a>
              <a
                href="/agbs"
                className="text-gray-500 hover:text-orange-600 text-sm transition-colors"
              >
                {language === "de" ? "AGB" : "Terms"}
              </a>
              <a
                href="/#contact"
                className="text-gray-500 hover:text-orange-600 text-sm transition-colors"
              >
                {language === "de" ? "Kontakt" : "Contact"}
              </a>
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}
